import { useState, useEffect, useCallback } from 'react'
import { createRoot } from 'react-dom/client'
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, CloseOutlined } from '@ant-design/icons'

type MessageType = 'success' | 'error' | 'info'

interface MessageItem {
  id: string
  type: MessageType
  content: string
  duration?: number
}

// ─── 样式配置 ───────────────────────────────────────────────

const getTypeConfig = (type: MessageType) => {
  switch (type) {
    case 'success':
      return {
        icon: <CheckCircleFilled />,
        gradient: 'linear-gradient(135deg, #10B981, #059669)',
        bg: 'rgba(16, 185, 129, 0.15)',
        border: 'rgba(16, 185, 129, 0.3)',
        text: '#10B981',
      }
    case 'error':
      return {
        icon: <CloseCircleFilled />,
        gradient: 'linear-gradient(135deg, #EF4444, #DC2626)',
        bg: 'rgba(239, 68, 68, 0.15)',
        border: 'rgba(239, 68, 68, 0.3)',
        text: '#EF4444',
      }
    case 'info':
      return {
        icon: <InfoCircleFilled />,
        gradient: 'linear-gradient(135deg, #3B82F6, #2563EB)',
        bg: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.3)',
        text: '#3B82F6',
      }
  }
}

// ─── 单条消息组件 ───────────────────────────────────────────

interface MessageItemProps {
  item: MessageItem
  onRemove: (id: string) => void
  isMobile: boolean
}

function MessageItemComponent({ item, onRemove, isMobile }: MessageItemProps) {
  const [visible, setVisible] = useState(false)
  const [exiting, setExiting] = useState(false)
  const config = getTypeConfig(item.type)

  useEffect(() => {
    // 进入动画
    requestAnimationFrame(() => setVisible(true))

    // 自动消失
    const timer = setTimeout(() => {
      handleClose()
    }, item.duration || (item.type === 'success' ? 2000 : 3000))

    return () => clearTimeout(timer)
  }, [])

  const handleClose = useCallback(() => {
    setExiting(true)
    setTimeout(() => {
      onRemove(item.id)
    }, 300)
  }, [item.id, onRemove])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: isMobile ? '12px 16px' : '14px 20px',
        borderRadius: 12,
        background: '#1A1A2E',
        border: `1px solid ${config.border}`,
        boxShadow: `0 8px 24px rgba(0, 0, 0, 0.3), 0 0 0 1px ${config.border}`,
        backdropFilter: 'blur(12px)',
        minWidth: isMobile ? 'auto' : 280,
        maxWidth: isMobile ? '100%' : 400,
        width: isMobile ? '100%' : 'auto',
        transform: exiting
          ? 'translateY(-20px) scale(0.95)'
          : visible
          ? 'translateY(0) scale(1)'
          : 'translateY(-20px) scale(0.95)',
        opacity: exiting ? 0 : visible ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'auto',
      }}
    >
      {/* 图标 */}
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: config.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          color: config.text,
          flexShrink: 0,
        }}
      >
        {config.icon}
      </div>

      {/* 文字 */}
      <span
        style={{
          flex: 1,
          fontSize: 14,
          color: '#E0E0E0',
          fontWeight: 500,
          lineHeight: 1.4,
        }}
      >
        {item.content}
      </span>

      {/* 关闭按钮 */}
      <button
        onClick={handleClose}
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#6B6B80',
          transition: 'all 0.2s',
          flexShrink: 0,
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
          e.currentTarget.style.color = '#A0A0B0'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
          e.currentTarget.style.color = '#6B6B80'
        }}
      >
        <CloseOutlined style={{ fontSize: 10 }} />
      </button>
    </div>
  )
}

// ─── 消息容器 ───────────────────────────────────────────────

let messageContainer: HTMLDivElement | null = null
let messageRoot: ReturnType<typeof createRoot> | null = null
let messages: MessageItem[] = []

function getContainer(): { root: ReturnType<typeof createRoot>; container: HTMLDivElement } {
  if (!messageContainer) {
    messageContainer = document.createElement('div')
    messageContainer.id = 'custom-message-container'
    messageContainer.style.cssText = `
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10000;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      pointer-events: none;
      padding: 0 16px;
      max-width: 100vw;
    `
    document.body.appendChild(messageContainer)
    messageRoot = createRoot(messageContainer)
  }
  return { root: messageRoot!, container: messageContainer }
}

function isMobile(): boolean {
  return window.innerWidth < 768
}

function renderMessages() {
  const { root } = getContainer()
  root.render(
    <MessageContainer
      messages={messages}
      onRemove={removeMessage}
      isMobile={isMobile()}
    />
  )
}

interface MessageContainerProps {
  messages: MessageItem[]
  onRemove: (id: string) => void
  isMobile: boolean
}

function MessageContainer({ messages, onRemove, isMobile: mobile }: MessageContainerProps) {
  return (
    <>
      {messages.map((msg) => (
        <MessageItemComponent
          key={msg.id}
          item={msg}
          onRemove={onRemove}
          isMobile={mobile}
        />
      ))}
    </>
  )
}

function removeMessage(id: string) {
  messages = messages.filter((msg) => msg.id !== id)
  if (messages.length === 0 && messageContainer) {
    messageRoot?.unmount()
    messageContainer.remove()
    messageContainer = null
    messageRoot = null
  } else {
    renderMessages()
  }
}

function addMessage(type: MessageType, content: string, duration?: number) {
  const id = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  const newMessage: MessageItem = { id, type, content, duration }

  messages = [...messages, newMessage]
  renderMessages()

  return id
}

// ─── 导出的 message 函数 ────────────────────────────────────

export const message = {
  success(content: string, duration?: number) {
    return addMessage('success', content, duration)
  },
  error(content: string, duration?: number) {
    return addMessage('error', content, duration)
  },
  info(content: string, duration?: number) {
    return addMessage('info', content, duration)
  },
  clear() {
    messages = []
    if (messageContainer) {
      messageRoot?.unmount()
      messageContainer.remove()
      messageContainer = null
      messageRoot = null
    }
  },
}
